/**
 * ScriptWriterNode — Canvas V2
 *
 * Displays script content, model variant tabs (Gemini/Claude),
 * structured screenplay rendering, conversation history, and review history.
 * Supports editing the screenplay via RichTextBox.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type WheelEvent as ReactWheelEvent } from 'react'
import { Handle, Position, useReactFlow, type NodeProps } from 'reactflow'
import { AArrowDown, AArrowUp, Palette, SlidersHorizontal, Wand2 } from 'lucide-react'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { useTranslation } from 'react-i18next'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { cn } from '../../../../lib/utils'
import { NODE_CARD_RADIUS_CLASS } from '../shared/constants'
import { executeNode, cancelNodeExecution, InsufficientSparksError } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { useProject } from '../../../../api/hooks'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useSetHoveredNodeId } from '../../NodeHoverContext'
import { dataStatus, dataStrOpt, type ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  useNodeActions,
  computeBorderStyle,
  RichTextBox,
  CREATIVE_TEXT_MAX_LENGTH,
  createHandlesFromSchema,
  QuickInfoBar,
  UnifiedBottomPanel,
  useBottomPanelAutoOpen,
  useSelectSingleCanvasNode,
  type TabDefinition,
  type ToolbarAction,
  type RichTextBoxHandle,
  type RichTextBoxState,
} from '../shared'
import { NODE_SCHEMAS_BY_TYPE, type NodeStatus, type ReactFlowCanvasNodeDataByType, type ScreenplayResultDictYMap, type VoiceProfileDictYMap, type VoiceRoleDictYMap } from '../../../../generated/nodeDataTypes'
import { NODE_BG_SWATCH_COLORS, resolveNodeBgColor, localizeNodeBgColor } from '../shared/nodeBgColors'
import { parseScreenplayText, IMPLICIT_SCENE_LOCATION } from './parseScreenplayText'
import { ScriptStoryHeader, type StoryMetadataRow } from './ScriptStoryHeader'
import { ScriptVariantsPanel, getScriptVariantsPanelHeight, type ScriptVariantRow } from './ScriptVariantsPanel'
import { ScriptConfigTab, ScriptRefineTab, type ScriptModelKey } from './ScriptWriterPanelTabs'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { isOutputTruncationError, sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { getScriptModelLabel } from '../../../../constants/modelAliases'
import { trackGenerationCancelled } from '../../../../analytics/interactionAnalytics'

const streamLog = createDebug('dw:script-streaming:render')
const errLog = createDebug('dw:script-writer:error')
// NAD feature trace for the output-length-truncation banner UX (post 1578153260608638).
const truncationLog = createNadDebug('dw:script-writer-truncation-ux:banner')
const configLog = createDebug('dw:script-writer:config')
const scriptPlusRemovalLog = createDebug('dw:script-plus-removal:ui')
// NAD feature tag script-edit-resync: inline screenplay edits re-sync the
// structured outputs on save (Workplace post 1573178877772743).
const editResyncLog = createDebug('dw:script-writer:edit-resync')

// ─── Constants ────────────────────────────────────────────────────────────────

const NODE_WIDTH = 920
// Must match the scriptWriter layout height in schemas/node_defs.py. Capping the
// outer node here keeps FE-measured dimensions ≤ schema default so the
// layout engine and Director never under-reserve space for downstream nodes.
const NODE_MAX_HEIGHT = 620
// Reader font-scale bounds for the A- / A+ controls. The floor keeps the
// screenplay legible at canvas zoom-out; the ceiling stops a single scene from
// pushing the scroll area past the card's fixed height.
const FONT_SCALE_MIN = 0.8
const FONT_SCALE_MAX = 1.6
const FONT_SCALE_STEP = 0.1
// Synthetic Variants-row id prefix for canvases that predate
// `out_script_variants`. Selecting one switches the displayed model rather than
// loading a stored variant — see `handleSelectVariant`.
const LEGACY_VARIANT_PREFIX = 'legacy:'
const scriptWriterSchema = NODE_SCHEMAS_BY_TYPE.scriptWriter
type ScriptWriterNodeData = ReactFlowCanvasNodeDataByType['scriptWriter']

const agentColor = AGENT_COLORS.scriptwriter
const INVISIBLE_SIDE_HANDLE_CLASS = '!bg-transparent !border-0 !w-1 !h-4'
const INVISIBLE_TOP_BOTTOM_HANDLE_CLASS = '!bg-transparent !border-0 !w-4 !h-1'
const HANDLES = createHandlesFromSchema(scriptWriterSchema)

// ─── Types ────────────────────────────────────────────────────────────────────

type ModelKey = 'gemini' | 'claude'
type VoiceRole = VoiceRoleDictYMap
type VoiceProfile = VoiceProfileDictYMap
type RenderableScreenplayEntry = {
  type?: string
  character?: string
  emotion?: string
  voice_role_id?: string
  text?: string
}
type RenderableScreenplayScene = {
  scene_number?: number
  interior_exterior?: string
  location?: string
  time_of_day?: string
  entries?: RenderableScreenplayEntry[]
}

type ScriptNodeStatus = NodeStatus | 'needs-input'

type StatusAnimation = false | 'pulse' | 'spin' | 'spin-slow'

function getStatusIndicator(t: (key: string) => string): Record<ScriptNodeStatus, { color: string; label: string; animate: StatusAnimation }> {
  return {
    idle:                      { color: 'bg-text-muted/30', label: t('scriptWriterNode.statusReady'),           animate: false       },
    pending_execution:         { color: 'bg-warning',       label: t('scriptWriterNode.statusQueued'),          animate: 'spin-slow' },
    pending_cost_confirmation: { color: 'bg-warning',       label: t('scriptWriterNode.statusPendingApproval'), animate: false       },
    running:                   { color: 'bg-primary',       label: t('scriptWriterNode.statusGenerating'),      animate: 'spin'      },
    completed:                 { color: 'bg-success',       label: t('scriptWriterNode.statusDone'),            animate: false       },
    error:                     { color: 'bg-destructive',   label: t('scriptWriterNode.statusError'),           animate: false       },
    'needs-input':             { color: 'bg-amber-500',     label: t('scriptWriterNode.statusNeedsInput'),      animate: 'pulse'     },
  }
}

const isSupportedModelKey = (value: string | null | undefined): value is ModelKey =>
  value === 'gemini' || value === 'claude'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const safeString = (value: string | number | boolean | object | null | undefined): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try { return JSON.stringify(value) } catch { return String(value) }
}

function toScriptNodeStatus(value: string | undefined): ScriptNodeStatus {
  const normalized = value?.toLowerCase()
  return normalized === 'pending_execution' ||
    normalized === 'pending_cost_confirmation' ||
    normalized === 'running' ||
    normalized === 'completed' ||
    normalized === 'error' ||
    normalized === 'idle' ||
    normalized === 'needs-input'
    ? normalized
    : 'idle'
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const PencilIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
)

const CheckIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
)

const CopyIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
  </svg>
)

const TrashIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
)

// ─── Structured screenplay rendering ──────────────────────────────────────────

// `VoiceRole.kind` is an OPEN free-text field on the backend model
// (screenplay_models.py: `kind: str = "narrator"  # "narrator",
// "inner_monologue", "system", etc.`) — the LLM is not constrained to an enum.
// So this localizes the DISPLAY string keyed by the stored kind and falls back
// to today's humanized English for kinds we have no translation for, rather
// than leaking a raw i18n key onto the canvas. Same value/label split as
// localizeNodeBgColor. Keys live under `canvas.scriptWriterNode.voiceRoleKind.<kind>`.
// `ScreenplayScene.interior_exterior` is a CONTROLLED VOCABULARY, not prose:
// screenplay_models.py declares it as `str  # "INT", "EXT", "INT/EXT"`. It was
// being rendered raw into the scene header, so a ko/vi canvas read
// "장면 1 — INTERIOR. 카페". Localized as a display label, keyed off the stored
// value, exactly like the style slug.
//
// The lookup is normalized and covers the long forms ("INTERIOR"/"EXTERIOR")
// because the model demonstrably emits those despite the declared short
// vocabulary. Anything unrecognized falls through verbatim rather than being
// dropped — an unexpected value is still better shown than swallowed.
const INT_EXT_KEYS: Record<string, string> = {
  INT: 'int',
  INTERIOR: 'int',
  EXT: 'ext',
  EXTERIOR: 'ext',
  'INT/EXT': 'intExt',
  'INTERIOR/EXTERIOR': 'intExt',
  'EXT/INT': 'intExt',
}

const formatInteriorExterior = (
  value: string,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string => {
  const key = INT_EXT_KEYS[value.trim().toUpperCase().replace(/\.$/, '')]
  if (!key) return value
  return t(`scriptWriterNode.interiorExterior.${key}`, { defaultValue: value })
}

const formatVoiceRoleKind = (
  kind: string | undefined,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string => {
  if (!kind) return t('scriptWriterNode.voiceRoleKind.voice', { defaultValue: 'voice' })
  return t(`scriptWriterNode.voiceRoleKind.${kind}`, { defaultValue: kind.replace(/_/g, ' ') })
}

// ─── Screenplay typography ────────────────────────────────────────────────────
//
// The node renders a screenplay, so the body follows screenplay layout rather
// than the tinted entry cards this component used to draw: action flush left at
// full width, the character cue CENTRED, and the spoken line in an indented
// block under it.
//
// Figures read off the Figma node (133:241), as fractions of the 1168-wide card:
//   action / scene heading  text x=48            -> 4.1% inset  (the body px-[4.1%])
//   character cue           text x=96,  w=976    -> centred
//   dialogue                text x=195, w=778    -> 66.6% wide, 16.7% each side
// The body already carries the 4.1% inset, so 66.6% of the CARD is 72.5% of the
// body's content box (0.666 / 0.918). Everything is a percentage, so it holds at
// any card width and under any font scale.
//
// Every size is a calc() against `--sw-font-scale` so the A- / A+ controls move
// the whole body together — see the scroll containers in renderScriptContent.
const SPEECH_CUE_CLASS =
  'text-center font-bold uppercase tracking-wider text-amber-400/90 ' +
  'text-[calc(10px*var(--sw-font-scale,1))] leading-[calc(15px*var(--sw-font-scale,1))]'
// The spoken line is centre-ALIGNED as well as centre-positioned, on request.
// Note this is a deliberate departure from Figma 133:241, where the dialogue
// block is an indented column of left-aligned, ragged-right text. Flip
// `text-center` back to the default here to restore the design's version.
const SPEECH_PARENTHETICAL_CLASS =
  'mx-auto w-[72.5%] text-center text-text-muted ' +
  'text-[calc(10px*var(--sw-font-scale,1))] leading-[calc(14px*var(--sw-font-scale,1))]'
const SPEECH_BODY_CLASS =
  'mx-auto w-[72.5%] text-center text-foreground/80 leading-relaxed ' +
  'text-[calc(10px*var(--sw-font-scale,1))]'

const SceneEntry = ({
  entry,
  voiceRolesById,
}: {
  entry: RenderableScreenplayEntry
  voiceRolesById: Map<string, VoiceRole>
}) => {
  const { t } = useTranslation('canvas')
  const entryType = entry.type
  const character = entry.character
  const emotion = entry.emotion
  const voiceRoleId = entry.voice_role_id
  const text = entry.text ?? ''

  if (entryType === 'dialogue' || entryType === 'os') {
    return (
      <div className="space-y-0.5">
        {character && (
          <p className={SPEECH_CUE_CLASS}>
            {character}
            {entryType === 'os' && ' (O.S.)'}
          </p>
        )}
        {emotion && <p className={SPEECH_PARENTHETICAL_CLASS}>({emotion})</p>}
        <p className={SPEECH_BODY_CLASS}>{text}</p>
      </div>
    )
  }

  if (entryType === 'voiceover') {
    const role = voiceRoleId ? voiceRolesById.get(voiceRoleId) : undefined
    const displayName = role?.label ?? character ?? voiceRoleId ?? t('scriptWriterNode.voiceover')

    return (
      <div className="space-y-0.5">
        {/* The raw `voice_role_id` pill was removed: it is a stable snake_case
            IDENTIFIER ("eli_inner_monologue"), not display copy, and the human
            `role.label` for the same role already renders here. It read as
            untranslated English on a ko/vi canvas because it is not prose in
            any language. The kind now rides the cue as a screenplay extension
            — `PROTAGONIST (V.O.)` in the mock — which is where a reader of a
            screenplay expects it. */}
        <p className={SPEECH_CUE_CLASS}>
          {displayName} ({formatVoiceRoleKind(role?.kind, t)})
        </p>
        {emotion && <p className={SPEECH_PARENTHETICAL_CLASS}>({emotion})</p>}
        <p className={SPEECH_BODY_CLASS}>{text}</p>
      </div>
    )
  }

  if (entryType === 'sfx') {
    return (
      <div className="flex items-start gap-1.5">
        <span className="inline-block text-[calc(8px*var(--sw-font-scale,1))] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 font-medium uppercase tracking-wider flex-shrink-0 mt-px">
          {t('scriptWriterNode.sfx')}
        </span>
        <p className="text-[calc(10px*var(--sw-font-scale,1))] text-foreground/60 leading-relaxed">{text}</p>
      </div>
    )
  }

  if (entryType === 'music') {
    return (
      <div className="flex items-start gap-1.5">
        <span className="inline-block text-[calc(8px*var(--sw-font-scale,1))] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium uppercase tracking-wider flex-shrink-0 mt-px">
          {t('scriptWriterNode.music')}
        </span>
        <p className="text-[calc(10px*var(--sw-font-scale,1))] text-foreground/60 leading-relaxed">{text}</p>
      </div>
    )
  }

  // action (default)
  return (
    <p className="text-[calc(10px*var(--sw-font-scale,1))] text-foreground/70 leading-relaxed">{text}</p>
  )
}

const SceneCard = ({
  scene,
  voiceRolesById,
  isStreaming,
}: {
  scene: RenderableScreenplayScene
  voiceRolesById: Map<string, VoiceRole>
  isStreaming?: boolean
}) => {
  const { t } = useTranslation('canvas')
  const sceneNumber = scene.scene_number
  const interiorExterior = scene.interior_exterior
  const location = scene.location
  const timeOfDay = scene.time_of_day
  const entries = scene.entries ?? []

  // `location` and `time_of_day` are free LLM-authored screenplay text and are
  // rendered verbatim. `interior_exterior` is NOT — it is a small controlled
  // vocabulary, so it localizes as a display label (see formatInteriorExterior).
  let header = sceneNumber == null
    ? t('scriptWriterNode.scene')
    : t('scriptWriterNode.sceneNumbered', { number: sceneNumber })
  if (interiorExterior) header += ` — ${formatInteriorExterior(interiorExterior, t)}`
  if (location) {
    // The "." here is the INT./EXT.-to-location separator. The English label
    // already carries that period ("INT."), so appending it unconditionally
    // rendered "INT.. AGENCY CREATIVE FLOOR" (Workplace post 1627216715702292).
    // Locales whose label is unpunctuated ("실내", "室內") still need it, so key
    // off what the header actually ends with rather than off the locale.
    const separator = header.endsWith('.') ? ' ' : '. '
    header += `${separator}${location === IMPLICIT_SCENE_LOCATION ? t('scriptWriterNode.unknownLocation') : location}`
  }
  if (timeOfDay) header += ` — ${timeOfDay}`

  // Music and SFX are pulled out of the model's ordering and shown as one group
  // directly under the scene heading, ahead of the spoken content. The model
  // scatters them between dialogue lines, which reads as interruptions; as a
  // block they read as the scene's soundbed, which is what they are.
  //
  // Only the cues move. Everything else keeps its original order, because
  // action and dialogue genuinely interleave and re-sorting those would rewrite
  // the scene rather than regroup it.
  const soundCues = entries.filter((e) => e.type === 'sfx' || e.type === 'music')
  const scriptedEntries = entries.filter((e) => e.type !== 'sfx' && e.type !== 'music')

  return (
    <div className="space-y-1.5">
      <p className="text-[calc(10px*var(--sw-font-scale,1))] font-bold uppercase tracking-wider text-amber-400/90">{header}</p>
      {soundCues.length > 0 && (
        <div className="space-y-1" data-testid="scene-sound-cues">
          {soundCues.map((entry, i) => (
            <SceneEntry key={`cue-${i}`} entry={entry} voiceRolesById={voiceRolesById} />
          ))}
        </div>
      )}
      {scriptedEntries.map((entry, i) => (
        <SceneEntry key={i} entry={entry} voiceRolesById={voiceRolesById} />
      ))}
      {isStreaming && (
        <span className="inline-block text-foreground/60 animate-[blink_1s_step-end_infinite]">
          &#9608;
        </span>
      )}
    </div>
  )
}

const VoiceRolesPanel = memo(function VoiceRolesPanel({
  voiceRoles,
  voiceProfilesByName,
}: {
  voiceRoles: VoiceRole[]
  voiceProfilesByName: Map<string, VoiceProfile>
}) {
  const { t } = useTranslation('canvas')
  const [isExpanded, setIsExpanded] = useState(false)

  if (voiceRoles.length === 0) return null

  return (
    <div className="border-b border-border-subtle" data-testid="script-voice-roles-panel">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-surface-1 nodrag"
        aria-expanded={isExpanded}
        data-testid="script-voice-roles-toggle"
      >
        <svg className="h-3 w-3 shrink-0 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <polyline points={isExpanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
        <span className="shrink-0 text-xs font-medium text-text-muted">{t('scriptWriterNode.narrators')}</span>
        {!isExpanded && (
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-secondary">
            {voiceRoles.map((role) => role.label).join(', ')}
          </span>
        )}
        <span className="shrink-0 text-xs font-medium text-text-muted">
          {voiceRoles.length}
        </span>
      </button>
      {isExpanded && (
        <div className="max-h-[120px] overflow-y-auto px-4 pb-3 pl-9 text-xs font-medium text-text-secondary nodrag nowheel" data-testid="script-voice-roles-expanded">
          {voiceRoles.map((role) => (
            <VoiceRoleRow
              key={role.id ?? ''}
              role={role}
              profile={voiceProfilesByName.get(role.id ?? '')}
            />
          ))}
        </div>
      )}
    </div>
  )
})

const VoiceRoleRow = memo(function VoiceRoleRow({
  role,
  profile,
}: {
  role: VoiceRole
  profile?: VoiceProfile
}) {
  const { t } = useTranslation('canvas')
  return (
    <div
      className="py-1"
      data-testid="script-voice-role-row"
    >
      <p className="truncate">
        <span className="font-medium text-foreground/80">{role.label}</span>
        {/* `role.id` dropped for the same reason as the voiceover pill — it is
            an internal identifier sitting next to its own human label. */}
        <span className="text-text-muted"> - {formatVoiceRoleKind(role.kind, t)}</span>
      </p>
      {profile?.voice_name && (
        <p className="mt-0.5 truncate text-text-muted">
          {t('scriptWriterNode.voiceProfile', { name: profile.voice_name })}
        </p>
      )}
      {role.voice_description && (
        <p className="mt-0.5 line-clamp-2 text-text-muted">
          {role.voice_description}
        </p>
      )}
    </div>
  )
})

// ─── Component ────────────────────────────────────────────────────────────────

export const ScriptWriterNode = memo(function ScriptWriterNode(props: NodeProps<ScriptWriterNodeData>) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { id, data, selected, dragging } = props
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const showInsufficientSparks = useShowInsufficientSparks()
  // Per-node 1s cooldown so a fast double-tap on Regenerate can't
  // fire /execute-node twice.
  const { isLocked: isRegenLockedKey, run: runRegenerate } = useClickLock(1000)
  const regenerateLocked = isRegenLockedKey(id)

  // Hooks
  const { setNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const node = useCanvasNode(id, 'scriptWriter', data)
  const handlesVisible = useHandlesVisibleForNode(id)
  const setHoveredNodeId = useSetHoveredNodeId()
  const { handleDelete, handleDuplicate, handleAddToChat } = useNodeActions({ nodeId: id })
  const richTextBoxRef = useRef<RichTextBoxHandle>(null)
  // The "Story" header titles itself with the project, not the node. React Query
  // dedupes this against WorkspacePageV2's own useProject call (same key), and
  // useUpdateProject patches that cache optimistically, so renaming the project
  // from the app header retitles every Script Writer on the canvas immediately.
  const { data: project } = useProject(projectId)

  // Read fields from CanvasNode wrapper
  const nodeName = node?.get('in_name') ?? ''
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, nodeName || t('scriptWriterNode.untitledScriptWriter'))
  const prompt = node?.get('in_prompt') ?? ''
  const script = node?.get('out_script')
  const scriptPath = node?.get('out_script_path')
  const activeModel = node?.get('in_active_model')
  const resolvedActiveModel = node?.get('out_active_model')
  const outGemini = node?.get('out_gemini')
  const outClaude = node?.get('out_claude')
  const legacyGptOutput = node?.get('out_gpt')
  const outVoiceRoles = node?.get('out_voice_roles')
  const outVoiceProfiles = node?.get('out_voice_profiles')
  const reviewHistory = node?.get('in_review_history') ?? []
  const outScriptVariants = node?.get('out_script_variants')
  const activeVariantId = node?.get('in_active_variant_id') ?? ''
  const outLogline = node?.get('out_logline')
  const outGenreTone = node?.get('out_genre_tone')
  const outSynopsis = node?.get('out_synopsis')
  const outThemes = node?.get('out_themes')
  const inGemini = node?.get('in_gemini')
  const inClaude = node?.get('in_claude')
  const status = dataStatus(data)
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const rawError = data.error
  const in_bg_color = node?.get('in_bg_color')

  const error = typeof rawError === 'string' ? rawError : rawError ? String(rawError) : undefined

  // NAD logging — record any error signal visible to the renderer so the
  // backend nad-debug.log timeline includes script_writer error states.
  // Fires on every render where any error-related field is non-empty so we can
  // diagnose canvas-vs-backend disagreements.
  useEffect(() => {
    const geminiErr = outGemini?.error
    const claudeErr = outClaude?.error
    if (!error && status !== 'error' && !geminiErr && !claudeErr) return
    errLog({
      node_id: id,
      status,
      error,
      gemini_error: geminiErr ? String(geminiErr) : undefined,
      claude_error: claudeErr ? String(claudeErr) : undefined,
    })
    // Output-length truncation (MAX_TOKENS / cut-off body): the banner now shows
    // actionable "make it shorter / split it" guidance instead of raw provider
    // jargon. The raw reason remains in the Director/backend trace, while every
    // UI surface stays behind the sanitizer. Emit a feature-scoped NAD trace so
    // the guidance branch is verifiable end-to-end.
    if (isOutputTruncationError(error)) {
      truncationLog({
        event: 'sanitized_truncation',
        node_id: id,
        status,
        banner: sanitizeNodeError(error, t, true),
        raw_reason: error,
      })
    }
  }, [id, status, error, outGemini, outClaude, t])

  useEffect(() => {
    const hasLegacyGptState =
      activeModel === 'gpt' ||
      resolvedActiveModel === 'gpt' ||
      legacyGptOutput != null

    if (!hasLegacyGptState) return

    scriptPlusRemovalLog({
      event: 'legacy-gpt-hidden',
      node_id: id,
      active_model: activeModel,
      resolved_active_model: resolvedActiveModel,
      has_legacy_gpt_output: legacyGptOutput != null,
      has_gemini_output: outGemini != null,
      has_claude_output: outClaude != null,
    })
  }, [id, activeModel, resolvedActiveModel, legacyGptOutput, outGemini, outClaude])

  // Local state
  const [isHovered, setIsHovered] = useState(false)
  const [showReviewHistory, setShowReviewHistory] = useState(false)
  const [localActiveModel, setLocalActiveModel] = useState<ModelKey | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editSessionKey, setEditSessionKey] = useState(0)
  const [showPrompt, setShowPrompt] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [copied, setCopied] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const colorPickerRef = useRef<HTMLDivElement>(null)
  // Reader font scale for the screenplay body, driven by the A- / A+ toolbar
  // buttons. Deliberately local state, not a node port: it is a per-viewer
  // reading preference, and persisting it to YDoc would push one collaborator's
  // font size onto everyone else on the canvas.
  const [fontScale, setFontScale] = useState(1)
  // Published as a CSS variable rather than a `fontSize`: SceneCard/SceneEntry
  // set absolute sizes (text-xxs, text-xs, ...), so an inherited font-size
  // never reaches them. Their sizes are calc()s against this variable.
  // Annotated, not cast: `apps/console` forbids every `as` expression, and
  // CSSProperties has no index signature for custom properties.
  const scriptFontScaleStyle: CSSProperties & { '--sw-font-scale': string } = useMemo(
    () => ({ '--sw-font-scale': String(fontScale) }),
    [fontScale],
  )
  // Snapshot of out_script when inline edit mode opened, used to detect a real
  // change on save so we only trigger the structured re-sync when needed.
  const editBaselineRef = useRef<string>('')

  const isRunning = status === 'running'
  const [isCancelling, setIsCancelling] = useState(false)
  const handleCancel = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    trackGenerationCancelled({ projectId, nodeId: id, nodeType: 'script_writer', stage: 'running' })
    cancelNodeExecution(projectId, id, canvasId)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => setIsCancelling(false))
  }, [isRunning, projectId, id, isCancelling, canvasId])

  // Regenerate screenplay with optional feedback
  const handleRegenerate = useCallback(async () => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || !projectId) return

    void runRegenerate(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  status: 'running',
                  progressMessage: t('scriptWriterNode.regeneratingScript'),
                  error: undefined,
                },
              }
            : n
        )
      )

      try {
        // Write feedback to YDoc before executing
        if (feedbackText.trim()) {
          node?.writeInFeedback(feedbackText.trim())
        }
        await executeNode(projectId, id, { canvasId })
        setFeedbackText('')
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({
            balance: err.balance,
            required: err.required,
            nodeId: id,
            nodeType: 'script_writer',
            projectId,
          })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n
            )
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    })
  }, [projectId, id, feedbackText, status, setNodes, node, runRegenerate, showInsufficientSparks, t, canvasId])

  // Every state `handleReplaceScript` refuses, so the button is visibly
  // disabled rather than silently inert when the panel stays open through a
  // queued or cost-gated run.
  const replaceDisabled =
    status === 'running' ||
    status === 'pending_execution' ||
    status === 'pending_cost_confirmation' ||
    regenerateLocked ||
    !projectId

  // Take the Refine box contents as the new screenplay outright.
  //
  // The Refine path (`handleRegenerate`) writes `in_feedback`, which the backend
  // reads as a targeted revision — it preserves the existing scene count and
  // every scene the note does not mention. A user who pastes a rewritten
  // screenplay with fewer scenes wants the opposite, and got 12 scenes back from
  // a 9-scene paste (Workplace post 1597061732051124). Replacing routes through
  // the schema-owned `replace_from_script` action: the backend parses the new
  // screenplay as authoritative input, drops scenes absent from it, and does
  // not make its safety depend on the old screenplay's inline round-trip guard.
  // No model call, no credits.
  const handleReplaceScript = useCallback(async () => {
    if (replaceDisabled) return
    const replacement = feedbackText.trim()
    if (!replacement) return

    // Inline editing can leave a failed re-sync's text in place because the user
    // typed it into the node and can see it. Replace cannot: the prior script is
    // only on screen until this write lands, so a failure here would destroy a
    // script the user never chose to discard, and leave `out_script` disagreeing
    // with the structured scenes the node still renders from. Roll back instead,
    // and keep the Refine box populated so the paste is not lost either.
    const previousScript = safeString(script)

    void runRegenerate(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  out_script: replacement,
                  status: 'running',
                  progressMessage: t('scriptWriterNode.syncingEdits'),
                  error: undefined,
                },
              }
            : n
        )
      )
      node?.writeOutScript(replacement)
      editResyncLog({
        event: 'refine-replace',
        node_id: id,
        new_len: replacement.length,
      })
      try {
        await executeNode(projectId, id, { action: 'replace_from_script', canvasId })
        setFeedbackText('')
      } catch (err) {
        node?.writeOutScript(previousScript)
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    out_script: previousScript,
                    status: 'error',
                    error: String(err),
                  },
                }
              : n
          )
        )
      }
    })
  }, [projectId, id, feedbackText, replaceDisabled, script, setNodes, node, runRegenerate, t, canvasId])

  // Generate screenplay for the first time (idle/error state)
  const handleGenerate = useCallback(async () => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || !projectId) return

    configLog({ event: 'generate-clicked', nodeId: id, hasPrompt: !!prompt })

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                status: 'running',
                progressMessage: t('scriptWriterNode.writingScript'),
                error: undefined,
              },
            }
          : n
      )
    )

    try {
      await executeNode(projectId, id, { canvasId })
    } catch (err) {
      if (err instanceof InsufficientSparksError) {
        showInsufficientSparks({
            balance: err.balance,
            required: err.required,
            nodeId: id,
            nodeType: 'script_writer',
            projectId,
          })
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
              : n
          )
        )
        return
      }
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
            : n
        )
      )
    }
  }, [projectId, id, prompt, status, setNodes, showInsufficientSparks, t, canvasId])

  // Prompt change handler for inline editing
  const handlePromptChange = useCallback(
    (value: string) => {
      configLog({ event: 'prompt-changed', nodeId: id, promptLength: value.length })
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_prompt: value } } : n
        )
      )
      node?.writeInPrompt(value)
    },
    [id, setNodes, node],
  )

  // Model variant state
  const hasVariants = !!(outGemini || outClaude)

  const currentActiveModel: ModelKey | null = useMemo(() => {
    if (!hasVariants) return null
    const supportedModelData: Record<ModelKey, ScreenplayResultDictYMap | undefined> = {
      gemini: outGemini,
      claude: outClaude,
    }
    if (localActiveModel && supportedModelData[localActiveModel]) return localActiveModel
    if (isSupportedModelKey(activeModel) && supportedModelData[activeModel]) return activeModel
    if (isSupportedModelKey(resolvedActiveModel) && supportedModelData[resolvedActiveModel]) {
      return resolvedActiveModel
    }
    if (outGemini && !outGemini.error) return 'gemini'
    if (outClaude && !outClaude.error) return 'claude'
    return null
  }, [hasVariants, localActiveModel, activeModel, resolvedActiveModel, outGemini, outClaude])

  const activeModelData = useMemo(() => {
    if (!hasVariants || !currentActiveModel) return null
    return { gemini: outGemini, claude: outClaude }[currentActiveModel] ?? null
  }, [hasVariants, currentActiveModel, outGemini, outClaude])

  // ── Script variants (iteration history) ───────────────────────────────────

  const storedVariants = useMemo(() => outScriptVariants ?? [], [outScriptVariants])
  const usingLegacyVariants = storedVariants.length === 0

  // Canvases generated before `out_script_variants` existed still hold one or
  // two per-model results. Synthesize rows for them so the Variants panel is not
  // empty on an existing project and, more importantly, so switching between the
  // Gemini and Claude results stays possible now that the model tabs that used
  // to do that job have moved into the Config panel.
  const legacyModelVariants: ScriptVariantRow[] = useMemo(() => {
    const built: { row: ScriptVariantRow; key: ModelKey }[] = []
    const sources: { key: ModelKey; data: ScreenplayResultDictYMap | undefined }[] = [
      { key: 'gemini', data: outGemini },
      { key: 'claude', data: outClaude },
    ]
    for (const { key, data: modelData } of sources) {
      if (!modelData || modelData.error) continue
      built.push({
        key,
        row: {
          id: `${LEGACY_VARIANT_PREFIX}${key}`,
          ordinal: built.length + 1,
          label: '',
          testId: `script-variant-legacy-${key}`,
        },
      })
    }
    // One result means one run stands behind this node, so "Script 1" is
    // accurate rather than an invented run number — and it makes the row
    // indistinguishable from how a single stored variant renders, which is what
    // these nodes become the moment they are next regenerated.
    if (built.length === 1) return [built[0].row]
    // Two results came from ONE run across two models. "Script 1 / Script 2"
    // would claim two runs that never happened, so name the model instead.
    return built.map(({ row, key }) => ({ ...row, titleOverride: getScriptModelLabel(key) }))
  }, [outGemini, outClaude])

  const variantRows: ScriptVariantRow[] = useMemo(() => {
    if (usingLegacyVariants) return legacyModelVariants
    return storedVariants.map((variant, index) => {
      const ordinal = variant.ordinal ?? index + 1
      return {
        id: variant.id ?? `variant-${index}`,
        ordinal,
        label: safeString(variant.label),
        testId: `script-variant-${ordinal}`,
      }
    })
  }, [usingLegacyVariants, legacyModelVariants, storedVariants])

  /** The newest stored variant for the model currently being displayed. */
  const masterVariant = useMemo(() => {
    if (usingLegacyVariants) return null
    for (let i = storedVariants.length - 1; i >= 0; i -= 1) {
      if (storedVariants[i].model === currentActiveModel) return storedVariants[i]
    }
    return storedVariants[storedVariants.length - 1] ?? null
  }, [storedVariants, currentActiveModel, usingLegacyVariants])

  /**
   * The variant the user picked, when it is NOT the master. Viewing an older
   * entry is a read-only preview: `out_script` still holds the master, and
   * inline editing stays disabled so a preview can never overwrite it.
   */
  const previewVariant = useMemo(() => {
    if (usingLegacyVariants || !activeVariantId) return null
    const picked = storedVariants.find((variant) => variant.id === activeVariantId)
    if (!picked || picked.id === masterVariant?.id) return null
    return picked
  }, [usingLegacyVariants, activeVariantId, storedVariants, masterVariant])

  const activeScenes: RenderableScreenplayScene[] | null = useMemo(() => {
    if (previewVariant) {
      const previewScenes = previewVariant.screenplay ?? []
      return previewScenes.length > 0 ? previewScenes : null
    }
    const modelData = activeModelData
    if (!modelData || modelData.error) return null
    const scenes = modelData.scenes ?? []
    return scenes && scenes.length > 0 ? scenes : null
  }, [previewVariant, activeModelData])

  /** Screenplay text currently on screen — the preview when one is open. */
  const displayScript = previewVariant ? previewVariant.script : script

  /**
   * Row the Variants panel outlines as the master. On a legacy canvas there is
   * no `in_active_variant_id`, so the displayed MODEL is what "active" means.
   */
  const activeVariantRowId = usingLegacyVariants
    ? `${LEGACY_VARIANT_PREFIX}${currentActiveModel ?? 'gemini'}`
    : activeVariantId

  const activeVoiceRoles: VoiceRole[] = useMemo(() => {
    if (outVoiceRoles && outVoiceRoles.length > 0) return outVoiceRoles
    if (activeModelData && !activeModelData.error) {
      const roles = activeModelData.voice_roles ?? []
      if (roles.length) return roles
    }
    return []
  }, [activeModelData, outVoiceRoles])

  const voiceRolesById = useMemo(
    () => new Map(activeVoiceRoles.filter((role) => role.id).map((role) => [role.id!, role])),
    [activeVoiceRoles],
  )
  const voiceProfilesByName = useMemo(() => {
    const entries: Array<[string, VoiceProfile]> = []
    for (const profile of outVoiceProfiles ?? []) {
      const name = profile.name
      if (name) entries.push([name, profile])
    }
    return new Map(entries)
  }, [outVoiceProfiles])

  // Parse streaming text into progressive scene cards during generation
  const streamingScenes = useMemo(() => {
    if (!isRunning || !script) return null
    const parsed = parseScreenplayText(safeString(script))
    if (parsed.length > 0) {
      streamLog('streaming nodeId=%s scenes=%d scriptLen=%d', id, parsed.length, safeString(script).length)
    }
    return parsed
  }, [isRunning, script, id])

  // Fallback: parse out_script into scene cards when structured data hasn't arrived yet
  const fallbackScenes = useMemo(() => {
    if (isRunning || activeScenes || !displayScript) return null
    const parsed = parseScreenplayText(safeString(displayScript))
    return parsed.length > 0 ? parsed : null
  }, [isRunning, activeScenes, displayScript])

  // Story metadata header rows. A preview carries its own metadata so the header
  // describes the screenplay on screen; otherwise the out_* ports (which the
  // agent resolves from the active model) are the source.
  const storyMetadataRows: StoryMetadataRow[] = useMemo(() => {
    const source = previewVariant ?? {
      logline: outLogline,
      genre_tone: outGenreTone,
      synopsis: outSynopsis,
      themes: outThemes,
    }
    const candidates: StoryMetadataRow[] = [
      { id: 'logline', value: safeString(source.logline).trim() },
      { id: 'genreTone', value: safeString(source.genre_tone).trim() },
      { id: 'synopsis', value: safeString(source.synopsis).trim() },
      { id: 'themes', value: safeString(source.themes).trim() },
    ]
    return candidates.filter((row) => row.value.length > 0)
  }, [previewVariant, outLogline, outGenreTone, outSynopsis, outThemes])

  // Agent status – normalize to lowercase to handle case-insensitive backend values
  const agentStatus = toScriptNodeStatus(status)

  const statusIndicator = useMemo(() => getStatusIndicator(t), [t])
  const indicator = statusIndicator[agentStatus] ?? statusIndicator.idle

  // Border style
  const borderStyle = computeBorderStyle(!!selected, status as NodeStatus, isHovered, agentColor, in_bg_color)

  // ── Handlers ──

  /** Prevent wheel events from propagating to the canvas (scroll trapping). */
  const stopWheelPropagation = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }, [])

  const handleTabClick = useCallback(
    (modelKey: ModelKey) => {
      if (modelKey === currentActiveModel) return
      setLocalActiveModel(modelKey)
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_active_model: modelKey } } : n
        )
      )
      node?.writeInActiveModel(modelKey)
    },
    [id, node, currentActiveModel, setNodes]
  )

  // ── Bottom panel ──────────────────────────────────────────────────────────

  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('config')
  const variantsMode = activeTab === 'variants'
  const panelVisible = !!selected && !dragging && bottomPanelOpen
  const hasAnyScript = !!(activeScenes || displayScript)
  const selectSingleNode = useSelectSingleCanvasNode()

  useBottomPanelAutoOpen({
    selected: selected,
    isRunning,
    // The Script Writer has no per-run batch fan-out, so there is nothing for
    // the shared hook's "open Variants while generating" branch to react to.
    batchPending: 0,
    setBottomPanelOpen,
    setActiveTab,
  })

  const panelTabs: TabDefinition[] = useMemo(
    () => [
      { id: 'config', label: 'Config', icon: SlidersHorizontal },
      { id: 'refine', label: 'Refine', icon: Wand2 },
    ],
    [],
  )

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      selectSingleNode(id)
    },
    [id, selectSingleNode],
  )

  /**
   * Model the NEXT run will use. `in_gemini`/`in_claude` are the generation
   * toggles; `in_active_model` only picks which finished result is displayed.
   * When the Director has enabled both models the chip shows whichever result
   * is on screen, so the label always matches what the user is reading.
   */
  const configuredModel: ScriptModelKey = useMemo(() => {
    if (inGemini && !inClaude) return 'gemini'
    if (inClaude && !inGemini) return 'claude'
    return currentActiveModel ?? 'gemini'
  }, [inGemini, inClaude, currentActiveModel])

  const handleModelChange = useCallback(
    (model: ScriptModelKey) => {
      const nextGemini = model === 'gemini'
      const nextClaude = model === 'claude'
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  in_gemini: nextGemini,
                  in_claude: nextClaude,
                  in_active_model: model,
                },
              }
            : n,
        ),
      )
      node?.writeInGemini(nextGemini)
      node?.writeInClaude(nextClaude)
      node?.writeInActiveModel(model)
      setLocalActiveModel(model)
      configLog({ event: 'model-changed', nodeId: id, model })
    },
    [id, node, setNodes],
  )

  const handleSelectVariant = useCallback(
    (variantId: string) => {
      // Legacy rows stand in for a per-model result rather than a stored
      // variant, so selecting one switches the displayed MODEL instead.
      if (variantId.startsWith(LEGACY_VARIANT_PREFIX)) {
        const modelKey = variantId.slice(LEGACY_VARIANT_PREFIX.length)
        if (isSupportedModelKey(modelKey)) handleTabClick(modelKey)
        return
      }
      const picked = storedVariants.find((variant) => variant.id === variantId)
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_active_variant_id: variantId } } : n,
        ),
      )
      node?.writeInActiveVariantId(variantId)
      // Keep the model in sync so the Config chip and the screenplay agree.
      if (picked && isSupportedModelKey(picked.model)) setLocalActiveModel(picked.model)
    },
    [id, node, setNodes, storedVariants, handleTabClick],
  )

  const handleScriptChange = useCallback(
    (state: RichTextBoxState) => {
      const newScript = state.plainText
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, out_script: newScript } } : n
        )
      )
      node?.writeOutScript(newScript)
    },
    [id, setNodes, node]
  )

  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      node?.writeInName(newName)
    },
    [id, setNodes, node],
  )

  const handleEnterEditMode = useCallback(() => {
    editBaselineRef.current = safeString(script)
    setIsEditing(true)
    setEditSessionKey((k) => k + 1)
  }, [script])

  // Leaving inline edit mode. If the user actually changed the screenplay text,
  // trigger the backend `resync_from_script` action so the structured outputs
  // (rendered scene view + downstream storyboard/video) re-derive from the edit
  // instead of drifting from it (Workplace post 1573178877772743). This is a
  // deterministic, zero-cost action — no model call, no credits.
  const handleExitEditMode = useCallback(() => {
    setIsEditing(false)
    const nextScript = safeString(script)
    const changed = nextScript !== editBaselineRef.current
    editResyncLog({
      event: 'exit-edit',
      node_id: id,
      changed,
      baseline_len: editBaselineRef.current.length,
      new_len: nextScript.length,
    })
    if (!changed || !projectId) return
    void runRegenerate(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  status: 'running',
                  progressMessage: t('scriptWriterNode.syncingEdits'),
                  error: undefined,
                },
              }
            : n
        )
      )
      try {
        await executeNode(projectId, id, { action: 'resync_from_script', canvasId })
      } catch (err) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    })
  }, [id, script, projectId, canvasId, setNodes, runRegenerate, t])

  const handleCopyScript = useCallback(() => {
    // Copy what is on screen — when a historical variant is open, copying the
    // master instead would silently hand back different text than the user read.
    const text = safeString(displayScript)
    if (!text) return
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }, [displayScript])

  // ── Background color ──

  const handleBgColorChange = useCallback(
    (colorKey: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_bg_color: colorKey } } : n,
        ),
      )
      node?.writeInBgColor(colorKey)
      setShowColorPicker(false)
    },
    [id, setNodes, node],
  )

  useEffect(() => {
    if (!showColorPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && e.target instanceof Node && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [showColorPicker])

  useEffect(() => {
    if (!selected) setShowColorPicker(false)
  }, [selected])

  // ── Toolbar ──

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'bg-color',
        label: t('scriptWriterNode.backgroundColor'),
        icon: <Palette size={14} />,
        onClick: () => setShowColorPicker((prev) => !prev),
      },
      {
        key: 'addToChat',
        label: t('scriptWriterNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
        group: true,
        nuxAnchorId: 'node-toolbar-add-to-chat',
        nuxControlId: 'addToChat',
      },
      {
        key: 'duplicate',
        label: t('scriptWriterNode.duplicate'),
        icon: <CopyIcon />,
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('scriptWriterNode.delete'),
        icon: <TrashIcon />,
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [duplicateMenuItems, handleDelete, handleAddToChat, t]
  )

  // ── Render helpers ──

  const resolvedProgressMessage = safeString(progressMessage) || t('scriptWriterNode.writingScript')

  const roleText = t('scriptWriterNode.roleText')

  const renderEditButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        handleEnterEditMode()
      }}
      title={t('scriptWriterNode.editScreenplay')}
      className="w-6 h-6 rounded hover:bg-surface-1 text-text-muted hover:text-foreground flex items-center justify-center transition-colors nodrag"
      data-testid="screenplay-edit-btn"
    >
      <PencilIcon />
    </button>
  )

  const renderCopyButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        handleCopyScript()
      }}
      title={t('scriptWriterNode.copyScreenplay')}
      className="w-6 h-6 rounded hover:bg-surface-1 text-text-muted hover:text-foreground flex items-center justify-center transition-colors nodrag"
      data-testid="screenplay-copy-btn"
      data-copied={copied ? 'true' : undefined}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  )

  // A- / A+ reader controls. The scale multiplies the screenplay body's font
  // size only; node chrome keeps its size so the card never reflows.
  const renderFontScaleButtons = () => (
    <div className="flex items-center rounded border border-border-subtle" data-testid="script-font-scale">
      <button
        onClick={(e) => {
          e.stopPropagation()
          setFontScale((prev) => Math.max(FONT_SCALE_MIN, Number((prev - FONT_SCALE_STEP).toFixed(2))))
        }}
        disabled={fontScale <= FONT_SCALE_MIN}
        title={t('scriptWriterNode.decreaseFontSize')}
        aria-label={t('scriptWriterNode.decreaseFontSize')}
        className="w-6 h-6 rounded-l text-text-muted hover:bg-surface-1 hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent flex items-center justify-center transition-colors nodrag"
        data-testid="script-font-decrease"
      >
        <AArrowDown size={16} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation()
          setFontScale((prev) => Math.min(FONT_SCALE_MAX, Number((prev + FONT_SCALE_STEP).toFixed(2))))
        }}
        disabled={fontScale >= FONT_SCALE_MAX}
        title={t('scriptWriterNode.increaseFontSize')}
        aria-label={t('scriptWriterNode.increaseFontSize')}
        className="w-6 h-6 rounded-r text-text-muted hover:bg-surface-1 hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent flex items-center justify-center transition-colors nodrag"
        data-testid="script-font-increase"
      >
        <AArrowUp size={16} />
      </button>
    </div>
  )

  const renderDoneButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        handleExitEditMode()
      }}
      title={t('scriptWriterNode.doneEditing')}
      className="w-6 h-6 rounded hover:bg-success/20 text-success hover:text-success flex items-center justify-center transition-colors nodrag"
      data-testid="screenplay-done-btn"
    >
      <CheckIcon />
    </button>
  )

  const renderScriptContent = () => {
    // Edit mode — RichTextBox
    if (isEditing && (activeScenes || script)) {
      return (
        <div className="w-full p-4 flex-1 min-h-0 flex flex-col nodrag nowheel" onWheel={stopWheelPropagation}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xxs uppercase tracking-wider text-text-muted">
              {t('scriptWriterNode.editingScreenplay')}
            </label>
            <div className="flex items-center gap-1">
              {renderCopyButton()}
              {renderDoneButton()}
            </div>
          </div>
          <RichTextBox
            key={editSessionKey}
            ref={richTextBoxRef}
            initialContent={safeString(script)}
            onContentChange={handleScriptChange}
            placeholder={t('scriptWriterNode.writeScreenplayPlaceholder')}
            flexFill
            maxLength={0}
            disabled={false}
            containerClassName="border border-border-subtle rounded-lg p-2 bg-surface-1 flex-1 min-h-0 nodrag nowheel"
          />
        </div>
      )
    }

    // Structured scenes — read-only SceneCard view
    if (activeScenes) {
      return (
        <div className="w-full py-4 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-2 px-[2.05%]">
            <label className="text-xxs uppercase tracking-wider text-text-muted">
              {previewVariant
                ? t('scriptWriterNode.viewingVariant', { number: previewVariant.ordinal ?? 0 })
                : t('scriptWriterNode.screenplaySceneCount', { count: activeScenes.length })}
            </label>
            <div className="flex items-center gap-1">
              {renderCopyButton()}
              {/* Editing writes back to out_script, i.e. the master. Offering it
                  while a historical variant is on screen would let a preview
                  overwrite the current script. */}
              {!previewVariant && renderEditButton()}
              {renderFontScaleButtons()}
            </div>
          </div>
          <div
            className="flex-1 min-h-0 overflow-y-auto px-[4.1%] nodrag nowheel space-y-3 font-mono"
            onWheel={stopWheelPropagation}
            style={scriptFontScaleStyle}
            data-testid="script-scene-scroll"
          >
            {activeScenes.map((scene, i) => (
              <SceneCard key={i} scene={scene} voiceRolesById={voiceRolesById} />
            ))}
          </div>
        </div>
      )
    }

    // Plain text fallback — parse into scene cards if possible
    if (displayScript) {
      if (fallbackScenes) {
        return (
          <div className="w-full py-4 flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-2 px-[2.05%]">
              <label className="text-xxs uppercase tracking-wider text-text-muted">
                {t('scriptWriterNode.screenplaySceneCount', { count: fallbackScenes.length })}
              </label>
              <div className="flex items-center gap-1">
                {renderCopyButton()}
                {!previewVariant && renderEditButton()}
                {renderFontScaleButtons()}
              </div>
            </div>
            <div
              className="flex-1 min-h-0 overflow-y-auto px-[4.1%] nodrag nowheel space-y-3 font-mono"
              onWheel={stopWheelPropagation}
              style={scriptFontScaleStyle}
              data-testid="script-scene-scroll"
            >
              {fallbackScenes.map((scene, i) => (
                <SceneCard key={i} scene={scene} voiceRolesById={voiceRolesById} />
              ))}
            </div>
          </div>
        )
      }
      return (
        <div className="w-full p-4 space-y-2 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-text-muted">{t('scriptWriterNode.script')}</div>
            <div className="flex items-center gap-2">
              {scriptPath && (
                <div className="text-xs font-medium text-success/60 font-mono truncate max-w-[200px]">
                  {safeString(scriptPath)}
                </div>
              )}
              {renderCopyButton()}
              {!previewVariant && renderEditButton()}
              {renderFontScaleButtons()}
            </div>
          </div>
          <div
            className="flex-1 min-h-0 overflow-y-auto pr-1 nodrag nowheel"
            onWheel={stopWheelPropagation}
            data-testid="script-scene-scroll"
          >
            <pre
              className="text-[calc(10px*var(--sw-font-scale,1))] text-text-secondary whitespace-pre-wrap leading-relaxed"
              style={scriptFontScaleStyle}
            >
              {safeString(displayScript)}
            </pre>
          </div>
        </div>
      )
    }

    // Empty state — editable config form for manually created nodes
    const canGenerate = agentStatus === 'idle' || agentStatus === 'error'
    return (
      <div className="w-full p-4 flex flex-col gap-3" data-testid="script-empty-config">
        {/* Prompt textarea */}
        <div className="space-y-1.5">
          <label className="text-xxs uppercase tracking-wider text-text-muted">{t('scriptWriterNode.storyPrompt')}</label>
          <textarea
            value={prompt}
            onChange={(e) => handlePromptChange(e.target.value.slice(0, CREATIVE_TEXT_MAX_LENGTH))}
            placeholder={t('scriptWriterNode.describeStory')}
            maxLength={CREATIVE_TEXT_MAX_LENGTH}
            rows={4}
            className={cn(
              'w-full px-3 py-2 text-xs font-medium bg-surface-1 border border-border-subtle rounded-lg',
              'text-foreground placeholder-text-muted/50',
              'focus:outline-none focus:border-primary/50 transition-colors resize-none',
              'nodrag nowheel'
            )}
            data-testid="script-prompt-input"
          />
        </div>

        {/* Generate button */}
        {canGenerate && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleGenerate()
            }}
            disabled={!prompt.trim()}
            className={cn(
              'w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors nodrag',
              prompt.trim()
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-surface-1 text-text-muted/50 cursor-not-allowed border border-border-subtle'
            )}
            data-testid="generate-script-btn"
          >
            {t('scriptWriterNode.generateScript')}
          </button>
        )}
      </div>
    )
  }

  /**
   * Stop control for an in-flight run.
   *
   * This used to live in the approval footer, which was gated on
   * `status === 'completed'` — so its `isRunning ? Stop : Regenerate` branch
   * could never actually render a Stop button. Refine and Regenerate have moved
   * into the bottom panel; cancel moves here, next to the running spinner, where
   * it is reachable for the first time.
   */
  const renderStopButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        handleCancel()
      }}
      disabled={isCancelling}
      className={cn(
        'shrink-0 rounded-md border px-2.5 py-1 text-xxs font-medium transition-colors nodrag',
        'border-red-500/50 text-red-400',
        isCancelling ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-500/10 hover:text-red-300',
      )}
      data-testid="cancel-node-button"
    >
      {isCancelling ? t('scriptWriterNode.stopping') : t('scriptWriterNode.stop')}
    </button>
  )

  return (
    <div className="relative">
      {/* Toolbar */}
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label */}
      <FloatingNodeLabel name={nodeName || ''} fallback={t('scriptWriterNode.untitledScriptWriter')} typeIcon={NODE_TYPE_ICON.scriptWriter} onRename={handleRename} bgColor={in_bg_color && in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[in_bg_color] : undefined} />

      {/* Color picker panel */}
      {showColorPicker && (
        <div
          ref={colorPickerRef}
          data-testid="script-writer-color-picker"
          style={{
            position: 'absolute',
            top: -52,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5 rounded-full bg-surface-2 backdrop-blur-md border border-border shadow-xl px-3 py-2">
            {Object.entries(NODE_BG_SWATCH_COLORS).map(([key, swatchColor]) => {
              const isActive = (in_bg_color ?? 'default') === key
              return (
                <button
                  key={key}
                  aria-label={localizeNodeBgColor(key, t)}
                  title={localizeNodeBgColor(key, t)}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleBgColorChange(key)
                  }}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: swatchColor,
                    border: isActive ? '2px solid white' : '2px solid transparent',
                    cursor: 'pointer',
                    flexShrink: 0,
                    outline: 'none',
                    boxShadow: isActive ? '0 0 0 1px rgba(255,255,255,0.3)' : 'none',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Card */}
      <div
        data-nux-anchor="node-visible"
        data-nux-node-id={id}
        data-nux-node-type="scriptWriter"
        data-nux-node-status={status}
        className={cn(
          'canvas-node-card-contained border transition-all duration-200 overflow-hidden cursor-pointer flex flex-col',
          NODE_CARD_RADIUS_CLASS,
          (agentStatus === 'running' || agentStatus === 'pending_execution' || agentStatus === 'pending_cost_confirmation') && 'animate-node-shimmer',
        )}
        style={{ width: nodePresentationWidth(NODE_WIDTH), maxHeight: NODE_MAX_HEIGHT, backgroundColor: resolveNodeBgColor(in_bg_color), ...borderStyle }}
        onMouseEnter={() => {
          setIsHovered(true)
          setHoveredNodeId(id)
        }}
        onMouseLeave={() => {
          setIsHovered(false)
          setHoveredNodeId(null)
        }}
      >
        {/* Invisible handles */}
        {handlesVisible && (
          <>
            <Handle type="target" position={Position.Top} id="in_upstream" className={INVISIBLE_TOP_BOTTOM_HANDLE_CLASS} />
            {HANDLES.map((handle) => (
              <Handle
                key={handle.id}
                type={handle.type}
                position={handle.position}
                id={handle.id}
                className={INVISIBLE_SIDE_HANDLE_CLASS}
              />
            ))}
            <Handle type="source" position={Position.Bottom} id="bottom" className={INVISIBLE_TOP_BOTTOM_HANDLE_CLASS} />
          </>
        )}

        {/* Story header — "Story" eyebrow, project title, status chip, and the
            collapsible Logline / Genre-Tone / Synopsis / Themes rows.
            The title is the PROJECT name, not `in_name`: a node rename retitles
            the floating node label only. Falls back to the role text while the
            project query is in flight or the project is unnamed. */}
        <ScriptStoryHeader
          title={project?.name ?? ''}
          titleFallback={roleText}
          accentColor={agentColor.active}
          rows={storyMetadataRows}
          statusChip={
            <span className="flex shrink-0 items-center gap-1.5 text-xxs font-medium text-text-muted">
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  agentStatus !== 'running' && indicator.color,
                  indicator.animate === 'pulse' && 'animate-pulse',
                  indicator.animate === 'spin' && 'animate-spin border border-t-transparent !rounded-full !bg-transparent',
                  indicator.animate === 'spin-slow' && 'animate-spin-slow border border-t-transparent !rounded-full !bg-transparent'
                )}
                style={
                  agentStatus === 'running'
                    ? { backgroundColor: agentColor.active }
                    : indicator.animate === 'spin' || indicator.animate === 'spin-slow'
                      ? { borderColor: 'var(--warning)', borderTopColor: 'transparent' }
                      : undefined
                }
              />
              {indicator.label}
            </span>
          }
        />

        {/* Content area */}
        {(agentStatus === 'running' || agentStatus === 'pending_execution' || agentStatus === 'pending_cost_confirmation') ? (
          <div className="bg-surface-1/50 flex-1 min-h-0 flex flex-col">
            {/* Compact spinner header */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border-subtle">
              <div
                className={`w-4 h-4 border-2 border-t-transparent rounded-full flex-shrink-0 ${
                  agentStatus === 'running' ? 'animate-spin' : agentStatus === 'pending_cost_confirmation' ? '' : 'animate-spin-slow'
                }`}
                style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
              />
              <span className="text-xxs text-text-muted flex-1">
                {agentStatus === 'running'
                  ? resolvedProgressMessage
                  : agentStatus === 'pending_cost_confirmation'
                    ? t('scriptWriterNode.waitingCostApprovalChat')
                    : t('scriptWriterNode.queuedWaiting')}
              </span>
              {isRunning && renderStopButton()}
            </div>
            {/* Progressive scene cards during streaming */}
            {agentStatus === 'running' && streamingScenes && streamingScenes.length > 0 ? (
              <div className="px-3 py-2 space-y-3 flex-1 min-h-0 overflow-y-auto nodrag nowheel font-mono" data-testid="streaming-scenes" onWheel={stopWheelPropagation}>
                {streamingScenes.map((scene, i) => (
                  <SceneCard
                    key={i}
                    scene={scene}
                    voiceRolesById={voiceRolesById}
                    isStreaming={i === streamingScenes.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div className="px-4 py-4 flex items-center justify-center">
                <span className="text-xxs text-text-muted">
                  {agentStatus === 'running' ? t('scriptWriterNode.starting') : ''}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="relative flex-1 min-h-0 flex flex-col">
            {/* Prompt display — collapsible read-only reference after script exists.
                In the empty state (no script), renderScriptContent() already shows
                the editable "Story Prompt" textarea, so skip this section. */}
            {prompt && (activeScenes || script) && (
              <div className="border-b border-border-subtle">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowPrompt((prev) => !prev)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left transition-colors hover:bg-surface-1 nodrag"
                  data-testid="script-prompt-toggle"
                  aria-expanded={showPrompt}
                >
                  <svg className="h-3 w-3 shrink-0 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points={showPrompt ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                  </svg>
                  <span className="shrink-0 text-xs font-medium text-text-muted">{t('scriptWriterNode.prompt')}</span>
                  {!showPrompt && (
                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-secondary">
                      {safeString(prompt)}
                    </span>
                  )}
                </button>
                {showPrompt && (
                  <div className="px-4 pb-3 pl-9 nodrag nowheel" onWheel={stopWheelPropagation} data-testid="script-prompt-expanded">
                    <div className="max-h-[120px] overflow-y-auto text-xs font-medium text-text-secondary">
                      {safeString(prompt)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Review history toggle */}
            {reviewHistory.length > 0 && (
              <div className="px-4 py-1.5 border-b border-border-subtle">
                <button
                  onClick={() => setShowReviewHistory(!showReviewHistory)}
                  className="flex items-center gap-1.5 text-xs font-medium text-text-muted hover:text-text-secondary transition-colors nodrag"
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points={showReviewHistory ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                  </svg>
                  <span>{showReviewHistory ? t('scriptWriterNode.hideReviewHistory', { count: reviewHistory.length }) : t('scriptWriterNode.showReviewHistory', { count: reviewHistory.length })}</span>
                  {reviewHistory.every((review) => review.accepted) ? (
                    <span className="text-xxs text-success/70 ml-auto">{t('scriptWriterNode.accepted')}</span>
                  ) : (
                    <span className="text-xxs text-amber-500/70 ml-auto">
                      {t('scriptWriterNode.revisionCount', { count: reviewHistory.filter((review) => !review.accepted).length })}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Review history panel */}
            {showReviewHistory && reviewHistory.length > 0 && (
              <div className="px-4 py-2 border-b border-border-subtle max-h-[200px] overflow-y-auto space-y-2 nodrag">
                {reviewHistory.map((review, i) => (
                  <div key={i} className="text-xs font-medium space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-text-secondary">{t('scriptWriterNode.attempt', { number: String(review.attempt ?? '') })}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-xxs font-medium ${
                          review.accepted
                            ? 'bg-success/15 text-success'
                            : 'bg-amber-500/15 text-amber-500'
                        }`}
                      >
                        {review.accepted ? t('scriptWriterNode.accepted') : t('scriptWriterNode.rejected')}
                      </span>
                    </div>
                    {review.feedback && (
                      <div className="text-text-muted pl-2 border-l-2 border-border-subtle">
                        {safeString(review.feedback).slice(0, 300)}
                        {safeString(review.feedback).length > 300 ? '...' : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Narrator / voice role summary */}
            <VoiceRolesPanel
              voiceRoles={activeVoiceRoles}
              voiceProfilesByName={voiceProfilesByName}
            />

            {/* Script content */}
            {renderScriptContent()}

            {/* Status overlay — floats over the bottom of the script instead of
                adding a footer, so a failure never changes the card's outline
                size (post 1634117708345526). */}
            {error && (
              <NodeStatusMessage
                message={sanitizeNodeError(error, t, true)}
                testId="script-writer-error"
              />
            )}
          </div>
        )}

        {/* Config | Variants footer strip — the entry point to the bottom panel. */}
        <QuickInfoBar
          // Figma 133:241 splits the card exactly in half (divider at 584 of
          // 1168). Safe here because this Config summary is only ever a short
          // model name, not the open-ended string the image nodes show.
          equalColumns
          configSummary={getScriptModelLabel(configuredModel)}
          variantCount={variantRows.length}
          // Deliberately NOT passing `activeColumn`. Its active treatment is
          // `bg-primary` (#D4A043) while every label inside the column hardcodes
          // its own colour — `text-muted-foreground` (#9CA3AF) on the labels,
          // `text-foreground` on the count — so the button's
          // `text-primary-foreground` never applies and the text sits
          // unreadably on amber. Script Writer was the only caller passing this;
          // every other node omits it, so this matches them.
          onOpenConfig={() => openPanelToTab('config')}
          onOpenVariants={() => openPanelToTab('variants')}
        />

      </div>

      {/* Bottom panel — sibling of the card, NOT a child of it.
          The card is `overflow-hidden`, and the panel is absolutely positioned at
          top:100%, i.e. entirely below the card's box — nesting it inside would
          clip it away completely. jsdom does not clip, so only a browser shows
          this. Same placement as ImageGeneratorNode/VideoGeneratorNode. */}
      <UnifiedBottomPanel
        tabs={panelTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isVisible={panelVisible}
        hideTabBar={variantsMode}
        minWidth={variantsMode ? 420 : 520}
        contentHeight={
          variantsMode ? getScriptVariantsPanelHeight(variantRows.length) : 260
        }
      >
        {(tab) => {
          if (tab === 'variants') {
            return (
              <ScriptVariantsPanel
                variants={variantRows}
                activeVariantId={activeVariantRowId}
                onSelectVariant={handleSelectVariant}
                accentColor={agentColor.active}
              />
            )
          }
          if (tab === 'refine') {
            return (
              <ScriptRefineTab
                feedbackText={feedbackText}
                onFeedbackTextChange={setFeedbackText}
                onSubmit={handleRegenerate}
                onReplaceScript={handleReplaceScript}
                replaceDisabled={replaceDisabled}
                isRunning={isRunning}
                submitDisabled={isRunning || regenerateLocked}
                model={configuredModel}
                onModelChange={handleModelChange}
              />
            )
          }
          return (
            <ScriptConfigTab
              prompt={safeString(prompt)}
              onPromptChange={handlePromptChange}
              model={configuredModel}
              onModelChange={handleModelChange}
              onGenerate={hasAnyScript ? handleRegenerate : handleGenerate}
              isRunning={isRunning}
              submitDisabled={isRunning || regenerateLocked || !safeString(prompt).trim()}
            />
          )
        }}
      </UnifiedBottomPanel>
    </div>
  )
})

ScriptWriterNode.displayName = 'ScriptWriterNode'
import { useNodePresentationWidth } from '../../NodePresentationContext'
